import React, { useCallback, useState } from 'react';
import { ErrorMessage, SmallBodyText, SpaceWrapper } from 'ui-toolkit';
import { ActualIncomingDetailContainer } from 'components/accounting/ActualIncomingDetailContainer';
import { ActualIncomingWiresSearchTable } from 'components/accounting/ActualIncomingWiresSearchTable';
import { createDateRangeFromDays } from 'utilities/accounting/createDateRangeFromDays';
import { IncomingTransactionLayout } from 'components/accounting/IncomingTransactionLayout';
import { IEscrowActualIncomingWireData } from 'types/accounting/data/IEscrowActualIncomingWireData';
import { IEscrowExpectedIncomingWireData } from 'types/accounting/data/IEscrowExpectedIncomingWireData';
import { IEscrowActualIncomingWireQuery } from 'types/accounting/query/IEscrowActualIncomingWireQuery';
import { eMatchedQueryValue } from 'types/accounting/eMatchedQueryValue';
import { createWireQueryFromAmountCents } from 'utilities/accounting/createQueryFromAmountCents';
import { IEscrowExpectedIncomingWireQuery } from 'types/accounting/query/IEscrowExpectedIncomingWireQuery';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { useQueryStringQuery } from 'pages/useQueryStringQuery';
import { createDefaultPageData } from 'types/rest/IPageData';
import { convertToError } from 'API/BaseApi';
import {
  useDeleteMatchWiresIncomingMutation,
  useGetWireIncomingActualQuery,
  useGetWiresIncomingActualQuery,
  useGetWiresIncomingExpectedQuery,
  usePostMatchWiresIncomingMutation,
} from 'API/accounting/AccountingApi';
import { createIFetchDataStateFromPieces } from './createIFetchDataStateFromPieces';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { PermissionWrapper } from '../../components/accessControl/PermissionWrapper';
import { PermissionNotification } from '../../components/accessControl/PermissionNotification';
import { useHasPermissions } from '../../components/accessControl/hooks/useHasPermissions';

const SIZE_PER_PAGE = 25;

export const defaultQuery: IEscrowActualIncomingWireQuery = {
  completedAtDate: createDateRangeFromDays(-60),
  matched: eMatchedQueryValue.UNMATCHED,
  pageNumber: 1,
  pageSize: SIZE_PER_PAGE,
  sortField: 'completedAtDate',
  sortOrder: 'desc',
};

export const SelectedDisplay = (selected: IEscrowActualIncomingWireData) => (
  <SpaceWrapper leftSpace={'none'} rightSpace={'none'}>
    {Object.keys(selected.wireDetail).map((key) => (
      <SmallBodyText key={key} data-test-id={key}>
        {key.replace('_', '-')}: {selected.wireDetail[key as keyof typeof selected.wireDetail]}
      </SmallBodyText>
    ))}
  </SpaceWrapper>
);

export const IncomingWiresPage: React.FC = () => {
  const hasExpectedReadPermission = useHasPermissions([ePermissions.EscrowExpectedIncomingWiresRead]);
  const hasActualReadPermission = useHasPermissions([ePermissions.EscrowActualIncomingWiresRead]);
  const skip = !(hasActualReadPermission && hasExpectedReadPermission);

  const hasActualEditPermission = useHasPermissions([ePermissions.EscrowActualIncomingWiresEdit]);
  const hasExpectedEditPermission = useHasPermissions([ePermissions.EscrowExpectedIncomingWiresEdit]);

  const { queryObject, setQueryObject } = useQueryStringQuery('wires', defaultQuery);

  const { data = createDefaultPageData(), error, isFetching } = useGetWiresIncomingActualQuery(queryObject, { skip });
  const fetchError = convertToError(error);

  const [suggestedQuery, setSuggestedQuery] = useState<IEscrowExpectedIncomingWireQuery>(
    {} as IEscrowExpectedIncomingWireQuery
  );

  const suggestedData = useGetWiresIncomingExpectedQuery(suggestedQuery, { skip });
  const suggestedExpected = createIFetchDataStateFromPieces({
    hookData: suggestedData,
    query: suggestedQuery,
    defaultData: createDefaultPageData<IEscrowExpectedIncomingWireData>(),
  });

  const setSuggestedExpectedQuery = useCallback(
    (query: IEscrowExpectedIncomingWireQuery) => setSuggestedQuery(query),
    [setSuggestedQuery]
  );

  const [selectedId, setSelectedId] = useState<string | undefined>();
  const { data: selected } = useGetWireIncomingActualQuery(selectedId || '', { skip: !selectedId });

  const [doMatch, { error: postError }] = usePostMatchWiresIncomingMutation();
  const onMatch = async (newMatch: IEscrowExpectedIncomingWireData) => {
    if (!selected || !hasActualEditPermission || !hasExpectedEditPermission) return;

    const result = await doMatch({ actualId: selected.id, expectedId: newMatch.id });

    checkForMutationSuccess(result, 'matching wire');
  };

  const [deleteMatch, { error: deleteError }] = useDeleteMatchWiresIncomingMutation();
  const onUnmatch = async (removeMatch: IEscrowExpectedIncomingWireData) => {
    if (!selected || !hasActualEditPermission || !hasExpectedEditPermission) return;

    const result = await deleteMatch({ actualId: selected.id, expectedId: removeMatch.id });

    checkForMutationSuccess(result, 'unmatching wire');
  };

  return (
    <PermissionWrapper
      allowedPermissions={[ePermissions.EscrowActualIncomingWiresRead, ePermissions.EscrowExpectedIncomingWiresRead]}
      requireAll={true}
    >
      <PermissionNotification
        allowedPermissions={[ePermissions.EscrowActualIncomingWiresEdit, ePermissions.EscrowExpectedIncomingWiresEdit]}
        requireAll={true}
      />
      <IncomingTransactionLayout
        table={
          <>
            <ErrorMessage
              data-test-id={'main-error-location'}
              error={fetchError || convertToError(postError) || convertToError(deleteError)}
            />
            <ActualIncomingWiresSearchTable
              isQueryFetching={isFetching}
              data={data}
              detail={selected}
              query={queryObject}
              setSelectedId={setSelectedId}
              setQuery={setQueryObject}
            />
          </>
        }
        sidecar={
          <>
            {selected && hasExpectedReadPermission && (
              <ActualIncomingDetailContainer
                createQueryFromAmountCents={createWireQueryFromAmountCents}
                getExpectedDataFromData={(data) => data.expectedIncomingWires || []}
                hasEditPermission={hasExpectedEditPermission && hasActualEditPermission}
                hasExpectedReadPermission={hasExpectedReadPermission}
                intro={'Select the matching deposit for this incoming wire.'}
                onMatch={onMatch}
                onUnmatch={onUnmatch}
                selectedIncomingActual={selected}
                setSuggestedExpectedQuery={setSuggestedExpectedQuery}
                showDetailsOfSelected={SelectedDisplay}
                suggestedExpectedResults={suggestedExpected}
                type={'wire'}
              />
            )}
          </>
        }
      />
    </PermissionWrapper>
  );
};
